import { useEffect, useState } from "react";
import {
  CalendarDays,
  Clock3,
  MapPin,
  Settings2,
  Users,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import type {
  ServiceOccurrence,
  ServiceOccurrenceUpdatePayload,
} from "@/types/service";

type Props = {
  open: boolean;
  service: ServiceOccurrence;
  isSubmitting: boolean;
  errorMessage?: string | null;
  onClose: () => void;
  onSubmit: (
    payload: ServiceOccurrenceUpdatePayload
  ) => Promise<void>;
};

type FormState = {
  name: string;
  serviceType: string;
  serviceDate: string;
  startTime: string;
  endTime: string;
  location: string;
  capacity: string;
  status: "SCHEDULED" | "COMPLETED" | "CANCELLED";
};

export function EditServiceDialog({
  open,
  service,
  isSubmitting,
  errorMessage,
  onClose,
  onSubmit,
}: Props) {
  const [form, setForm] = useState<FormState>(
    buildFormState(service)
  );

  const [validationError, setValidationError] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (open) {
      setForm(buildFormState(service));
      setValidationError(null);
    }
  }, [open, service]);

  if (!open) {
    return null;
  }

  async function handleSubmit(
    event: React.FormEvent<HTMLFormElement>
  ) {
    event.preventDefault();
    setValidationError(null);

    if (!form.name?.trim()) {
      setValidationError("Service name is required.");
      return;
    }

    if (!form.serviceDate) {
      setValidationError("Service date is required.");
      return;
    }

    if (!form.startTime) {
      setValidationError("Start time is required.");
      return;
    }

    if (
      form.endTime &&
      form.endTime <= form.startTime
    ) {
      setValidationError(
        "End time must be later than start time."
      );

      return;
    }

    const capacity = form.capacity
      ? Number(form.capacity)
      : null;

    if (
      capacity !== null &&
      (!Number.isInteger(capacity) || capacity < 1)
    ) {
      setValidationError(
        "Capacity must be a whole number greater than zero."
      );

      return;
    }

    await onSubmit({
      name: form.name.trim(),
      service_type: form.serviceType,
      starts_at: buildLocalDateTime(
        form.serviceDate,
        form.startTime
      ),
      ends_at: form.endTime
        ? buildLocalDateTime(
            form.serviceDate,
            form.endTime
          )
        : null,
      location: form.location?.trim() || null,
      capacity,
      status: form.status,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/80 px-4 py-8 backdrop-blur-sm">
      <div className="w-full max-w-3xl overflow-hidden rounded-3xl border border-slate-700 bg-slate-900 shadow-2xl shadow-black/50">
        <div className="relative border-b border-slate-800 px-7 py-6">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-500 via-violet-500 to-emerald-400" />

          <div className="flex items-start justify-between gap-5">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-500/15 text-indigo-400">
                <Settings2 size={22} />
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-white">
                  Edit Service
                </h2>

                <p className="mt-1 text-sm text-slate-400">
                  Update details or reschedule this occurrence.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-800 hover:text-white disabled:opacity-50"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-6 p-7 md:grid-cols-2">
            <FormField
              label="Service Name"
              icon={<CalendarDays size={16} />}
              className="md:col-span-2"
            >
              <input
                value={form.name}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                className={inputClassName}
              />
            </FormField>

            <FormField
              label="Service Type"
              icon={<Settings2 size={16} />}
            >
              <select
                value={form.serviceType}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    serviceType: event.target.value,
                  }))
                }
                className={inputClassName}
              >
                <option value="WORSHIP">
                  Worship
                </option>

                <option value="BIBLE_STUDY">
                  Bible Study
                </option>

                <option value="PRAYER">
                  Prayer
                </option>

                <option value="YOUTH">
                  Youth
                </option>

                <option value="SPECIAL">
                  Special Event
                </option>
              </select>
            </FormField>

            <FormField
              label="Status"
              icon={<Settings2 size={16} />}
            >
              <select
                value={form.status}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    status: event.target.value as FormState["status"],
                  }))
                }
                className={inputClassName}
              >
                <option value="SCHEDULED">
                  Scheduled
                </option>

                <option value="COMPLETED">
                  Completed
                </option>

                <option value="CANCELLED">
                  Cancelled
                </option>
              </select>
            </FormField>

            <FormField
              label="Service Date"
              icon={<CalendarDays size={16} />}
            >
              <input
                type="date"
                value={form.serviceDate}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    serviceDate: event.target.value,
                  }))
                }
                className={inputClassName}
              />
            </FormField>

            <FormField
              label="Location"
              icon={<MapPin size={16} />}
            >
              <input
                value={form.location}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    location: event.target.value,
                  }))
                }
                placeholder="Main Sanctuary"
                className={inputClassName}
              />
            </FormField>

            <FormField
              label="Start Time"
              icon={<Clock3 size={16} />}
            >
              <input
                type="time"
                value={form.startTime}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    startTime: event.target.value,
                  }))
                }
                className={inputClassName}
              />
            </FormField>

            <FormField
              label="End Time"
              icon={<Clock3 size={16} />}
            >
              <input
                type="time"
                value={form.endTime}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    endTime: event.target.value,
                  }))
                }
                className={inputClassName}
              />
            </FormField>

            <FormField
              label="Capacity"
              icon={<Users size={16} />}
              className="md:col-span-2"
            >
              <input
                type="number"
                min="1"
                step="1"
                value={form.capacity}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    capacity: event.target.value,
                  }))
                }
                placeholder="Optional"
                className={inputClassName}
              />
            </FormField>

            {(validationError || errorMessage) && (
              <div className="md:col-span-2 rounded-2xl border border-rose-500/20 bg-rose-500/5 px-5 py-4 text-sm text-rose-300">
                {validationError || errorMessage}
              </div>
            )}
          </div>

          <div className="flex flex-col-reverse gap-3 border-t border-slate-800 bg-slate-950/30 px-7 py-5 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "Saving..."
                : "Save Changes"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FormField({
  label,
  icon,
  className,
  children,
}: {
  label: string;
  icon: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={className}>
      <span className="flex items-center gap-2 text-sm font-medium text-slate-300">
        <span className="text-slate-500">
          {icon}
        </span>

        {label}
      </span>

      <span className="mt-2 block">
        {children}
      </span>
    </label>
  );
}

const inputClassName =
  "h-12 w-full rounded-xl border border-slate-700 bg-slate-950 px-4 text-sm text-white placeholder:text-slate-600 focus:border-indigo-500 focus:outline-none";

function buildFormState(
  service: ServiceOccurrence
): FormState {
  const startsAt = new Date(service.starts_at);
  const endsAt = service.ends_at
    ? new Date(service.ends_at)
    : null;

  return {
    name: service.title ?? "",
    serviceType: service.service_type,
    serviceDate: toLocalDateInput(startsAt),
    startTime: toLocalTimeInput(startsAt),
    endTime: endsAt
      ? toLocalTimeInput(endsAt)
      : "",
    location: service.location ?? "",
    capacity: service.capacity?.toString() ?? "",
    status: normalizeStatus(service.status),
  };
}

function normalizeStatus(
  value: string
): FormState["status"] {
  if (value === "COMPLETED") {
    return "COMPLETED";
  }

  if (value === "CANCELLED") {
    return "CANCELLED";
  }

  return "SCHEDULED";
}

function buildLocalDateTime(
  date: string,
  time: string
) {
  const localDateTime = new Date(
    `${date}T${time}:00`
  );

  return localDateTime.toISOString();
}

function toLocalDateInput(date: Date) {
  const year = date.getFullYear();

  const month = String(
    date.getMonth() + 1
  ).padStart(2, "0");

  const day = String(
    date.getDate()
  ).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function toLocalTimeInput(date: Date) {
  const hours = String(
    date.getHours()
  ).padStart(2, "0");

  const minutes = String(
    date.getMinutes()
  ).padStart(2, "0");

  return `${hours}:${minutes}`;
}